import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import { z } from "zod";
import { appDateTime } from "../src/app-dates.ts";
import {
  COULD_NOT_BOOK_REASONS,
  type CouldNotBookReason,
  LeadLinkTaskSchema,
} from "../src/lead-link-tasks.ts";
import { DateTimeField } from "./date-time-fields.tsx";
import { TimeZoneField } from "./time-zone-field.tsx";
import { usePlanApi, useWorkspace, workspaceApi } from "./workspace-context.tsx";

/*
 * The lead offered their own booking page (`docs/2026-10-01-lead-scheduling-link.md`, "The rep's
 * task"). The app wrote no draft; the rep books on the lead's page for the campaign's call host and
 * says what happened. The app never opens the page itself and never books: it hands over a link rebuilt
 * from scheme, host and path, and the call host's name and email (filled in on Calendly). Built in the
 * "no" and outside-goal sheets' style: one card, one primary action, quiet ways out.
 */

const TaskViewSchema = z.object({
  task: LeadLinkTaskSchema,
  callHost: z.object({ name: z.string(), email: z.string() }).nullable(),
  openUrl: z.string(),
  open: z.boolean(),
});
export type LeadLinkTaskView = z.infer<typeof TaskViewSchema>;

const SCHEDULERS: Record<LeadLinkTaskView["task"]["scheduler"], string> = {
  calendly: "Calendly",
  hubspot: "HubSpot",
  google: "Google Calendar",
  microsoft: "Microsoft Bookings",
  other: "Booking page",
};

/** Schedulers whose page may email a code to whoever books (the spike's table); HubSpot uses a CAPTCHA. */
const SENDS_CODE = new Set(["calendly", "google", "microsoft"]);

/** Why the page would not book, in the rep's words, and what to do next. */
const COULD_NOT_BOOK: Record<CouldNotBookReason, { label: string; next: string }> = {
  code_to_other_inbox: {
    label: "A code went to the call host's inbox",
    next: "Ask the call host for the code, then open their page again, or send our link.",
  },
  unanswerable_questions: {
    label: "The form asks what only the seller can answer",
    next: "Ask the call host, or send our link.",
  },
  routed_or_refused: {
    label: "Their page sent me elsewhere or turned me away",
    next: "Send our link instead.",
  },
  no_suitable_time: {
    label: "No time on their page suits the call host",
    next: "Send our link, or reply asking for other times.",
  },
  not_a_scheduler: {
    label: "The page is broken, gone or not a booking page",
    next: "Send our link instead.",
  },
  payment_required: {
    label: "Their page charges for the meeting",
    next: "Send our link instead.",
  },
  org_only: {
    label: "Their page only takes people from their company",
    next: "Send our link instead.",
  },
};

/** The lead's other asks, by label, each read after "They also asked about". */
const ASK_WORDS: Record<string, string> = {
  send_materials: "materials",
  answer_pricing: "pricing",
  confirm_scope: "scope",
  route_to_person: "who else to talk to",
  answer_data_source: "where we got their address",
  answer_technical: "a technical question",
  provide_references: "references",
  sign_nda: "an NDA",
  route_to_procurement: "procurement",
  stop_until_date: "waiting until a later date",
  other: "something else",
};

const displayUrl = (url: string): string => url.replace(/^https:\/\//u, "");
const firstName = (name: string): string => name.trim().split(/\s+/u)[0] ?? name;

/** The browser's own zone, which is the zone Calendly and Bookings show their times in. */
const browserZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "";
  }
};

type Step = "idle" | "booked" | "could_not_book";

/**
 * The one gold action follows the task: open their page; once this rep has opened it, mark it
 * booked; once their page would not book and the way on is ours, send our link. With nobody set to
 * take the call, saving the call host comes first, and opening the page stays available beside it.
 */
function primaryAction(input: {
  view: LeadLinkTaskView | null;
  viewerId: string | null;
  refused: CouldNotBookReason | null;
}): "open" | "booked" | "send" | "host" {
  if (input.view === null) return "open";
  if (input.view.callHost === null) return "host";
  if (input.refused && COULD_NOT_BOOK[input.refused].next.startsWith("Send our link"))
    return "send";
  if (input.view.task.claimedBy !== null && input.view.task.claimedBy === input.viewerId)
    return "booked";
  return "open";
}

export function LeadLinkSheet(props: {
  placement: "body" | "signature";
  view: LeadLinkTaskView | null;
  viewerId: string | null;
  busy: boolean;
  onOpen: () => void;
  onSaveHost: (host: { name: string; email: string }) => Promise<boolean>;
  onBooked: (booking: { start?: string; zone?: string; minutes?: number }) => Promise<void>;
  onCouldNotBook: (reason: CouldNotBookReason) => Promise<void>;
  onSendOurLink: () => void;
  onReply: () => void;
}) {
  const [step, setStep] = useState<Step>("idle");
  const [editingHost, setEditingHost] = useState(false);
  const [copied, setCopied] = useState<"name" | "email" | null>(null);
  // Focus returns to the control that opened a form, so the keyboard never lands on the page body.
  // The actions row mounts afresh when a form closes, so the opener is found again by name.
  const openers = useRef<Partial<Record<Exclude<Step, "idle">, HTMLButtonElement | null>>>({});
  const returnTo = useRef<Exclude<Step, "idle"> | null>(null);
  useEffect(() => {
    if (step !== "idle" || returnTo.current === null) return;
    openers.current[returnTo.current]?.focus();
    returnTo.current = null;
  }, [step]);
  const signature = props.placement === "signature";
  const view = props.view;
  const task = view?.task ?? null;
  const host = view?.callHost ?? null;
  const askingHost = view !== null && (host === null || editingHost);
  const otherAsks = (task?.otherAsks ?? []).map((ask) => ASK_WORDS[ask]).filter(Boolean);
  const refused = task?.outcome === "could_not_book" ? task.couldNotBookReason : null;
  const someoneElse = task?.claimedBy && task.claimedBy !== props.viewerId ? task.claimedAt : null;
  const primary = primaryAction({ view, viewerId: props.viewerId, refused });
  const opened = task?.claimedBy !== null && task?.claimedBy === props.viewerId;
  const back = () => {
    returnTo.current = step === "idle" ? null : step;
    setStep("idle");
  };
  const copy = (what: "name" | "email", value: string) => {
    void navigator.clipboard?.writeText(value).then(
      () => setCopied(what),
      () => setCopied(null),
    );
  };

  return (
    <article className="lcr-sheet lcr-lead-link" aria-labelledby="lcr-lead-link-heading">
      <h3 id="lcr-lead-link-heading">
        {signature
          ? "Booking page in their signature · book there or send ours"
          : "They sent their booking page · book there"}
      </h3>
      <p>
        {signature
          ? "No draft was written. Book a time on their page, or send our link instead."
          : "No draft was written: book a time on their page for the call host."}
      </p>
      {task && (
        <p className="lcr-lead-link-url">
          <span className="lcr-sheet-sub">{SCHEDULERS[task.scheduler]}</span>
          <span className="lcr-lead-link-address">{displayUrl(task.url)}</span>
        </p>
      )}
      {view === null && <p className="lcr-meta-text">Loading…</p>}
      {view !== null && !askingHost && host && task && (
        <div className="lcr-lead-link-host">
          <p>
            Book as <strong>{host.name}</strong>
            <span className="lcr-lead-link-email">{host.email}</span>
          </p>
          <div className="lcr-lead-link-copy">
            {task.scheduler === "calendly" ? (
              <span className="lcr-meta-text">
                Calendly fills in {firstName(host.name)}'s name and email.
              </span>
            ) : (
              <>
                <button
                  type="button"
                  className="lcr-linklike"
                  onClick={() => copy("name", host.name)}
                >
                  Copy name
                </button>
                <button
                  type="button"
                  className="lcr-linklike"
                  onClick={() => copy("email", host.email)}
                >
                  Copy email
                </button>
              </>
            )}
            <button type="button" className="lcr-linklike" onClick={() => setEditingHost(true)}>
              Change
            </button>
            <span className="lcr-meta-text" aria-live="polite">
              {copied ? `Copied the ${copied}.` : ""}
            </span>
          </div>
          {SENDS_CODE.has(task.scheduler) && (
            <p className="lcr-meta-text">
              {SCHEDULERS[task.scheduler]} may email {firstName(host.name)} a code. If you aren't{" "}
              {firstName(host.name)}, ask them for it.
            </p>
          )}
        </div>
      )}
      {askingHost && (
        <CallHostForm
          busy={props.busy}
          initial={host}
          onSave={async (value) => {
            if (await props.onSaveHost(value)) setEditingHost(false);
          }}
          onCancel={host ? () => setEditingHost(false) : null}
        />
      )}
      {otherAsks.length > 0 && (
        <p className="lcr-lead-link-asks">
          They also asked about {otherAsks.join(", ")}. Booking won't answer that: answer it on the
          call or in a reply.
        </p>
      )}
      {refused && (
        <p className="lcr-lead-link-refused">
          <strong>Couldn't book:</strong> {COULD_NOT_BOOK[refused].label.toLowerCase()}.{" "}
          {COULD_NOT_BOOK[refused].next}
        </p>
      )}
      {someoneElse && step === "idle" && (
        <p className="lcr-meta-text">Another rep opened their page {appDateTime(someoneElse)}.</p>
      )}
      {step === "booked" && (
        <BookedForm busy={props.busy} onSave={props.onBooked} onCancel={back} />
      )}
      {step === "could_not_book" && (
        <CouldNotBookForm
          busy={props.busy}
          onSave={async (reason) => {
            await props.onCouldNotBook(reason);
            back();
          }}
          onCancel={back}
        />
      )}
      {step === "idle" && (
        <div className="lcr-actions">
          {view ? (
            <a
              className={`lcr-button ${primary === "open" ? "lcr-primary" : "lcr-secondary-link"}`}
              href={view.openUrl}
              target="_blank"
              rel="noopener noreferrer"
              referrerPolicy="no-referrer"
              onClick={props.onOpen}
            >
              {opened ? "Open their page again" : "Open their page"}
            </a>
          ) : (
            <button type="button" className="lcr-primary" disabled>
              Open their page
            </button>
          )}
          <button
            type="button"
            ref={(node) => {
              openers.current.booked = node;
            }}
            className={primary === "booked" ? "lcr-primary" : undefined}
            disabled={props.busy || !view}
            onClick={() => setStep("booked")}
          >
            Mark booked
          </button>
          {!refused && (
            <button
              type="button"
              ref={(node) => {
                openers.current.could_not_book = node;
              }}
              disabled={props.busy || !view}
              onClick={() => setStep("could_not_book")}
            >
              Couldn't book
            </button>
          )}
          {(signature || refused) && (
            <button
              type="button"
              className={primary === "send" ? "lcr-primary" : undefined}
              disabled={props.busy}
              onClick={props.onSendOurLink}
            >
              Send our link
            </button>
          )}
        </div>
      )}
      <button type="button" className="lcr-linklike" disabled={props.busy} onClick={props.onReply}>
        Write a reply yourself <kbd className="lcr-kbd">R</kbd>
      </button>
    </article>
  );
}

function CallHostForm(props: {
  busy: boolean;
  initial: { name: string; email: string } | null;
  onSave: (host: { name: string; email: string }) => Promise<void>;
  onCancel: (() => void) | null;
}) {
  const [name, setName] = useState(props.initial?.name ?? "");
  const [email, setEmail] = useState(props.initial?.email ?? "");
  return (
    <form
      className="lcr-lead-link-form"
      onSubmit={(event) => {
        event.preventDefault();
        void props.onSave({ name: name.trim(), email: email.trim() });
      }}
    >
      <p className="lcr-lead-link-form-title">
        Who takes the call?{" "}
        <span className="lcr-sheet-sub">
          Their page books it for this person. Saved for this campaign.
        </span>
      </p>
      <div className="lcr-lead-link-fields">
        <label>
          Name
          <input
            type="text"
            value={name}
            required
            autoComplete="name"
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label>
          Email
          <input
            type="email"
            value={email}
            required
            autoComplete="email"
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
      </div>
      <div className="lcr-actions">
        <button
          type="submit"
          className="lcr-primary"
          disabled={props.busy || !name.trim() || !email.trim()}
        >
          Save call host
        </button>
        {props.onCancel && (
          <button type="button" className="lcr-linklike" onClick={props.onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

function BookedForm(props: {
  busy: boolean;
  onSave: (booking: { start?: string; zone?: string; minutes?: number }) => Promise<void>;
  onCancel: () => void;
}) {
  const [start, setStart] = useState("");
  const [zone, setZone] = useState(browserZone);
  const [minutes, setMinutes] = useState("");
  const length = Number(minutes);
  const id = useId();
  return (
    <form
      className="lcr-lead-link-form"
      onSubmit={(event) => {
        event.preventDefault();
        void props.onSave({
          ...(start ? { start, zone: zone.trim() } : {}),
          ...(minutes && Number.isInteger(length) ? { minutes: length } : {}),
        });
      }}
    >
      <p className="lcr-lead-link-form-title">
        What did their page book?{" "}
        <span className="lcr-sheet-sub">
          Optional: leave the time empty to just mark it booked.
        </span>
      </p>
      <div className="lcr-lead-link-fields">
        <div className="lcr-entry-slot">
          <label htmlFor={`${id}-start`}>Time shown on their page</label>
          {/* The form opens in place of the button that asked for it, so focus starts in it. */}
          <DateTimeField
            id={`${id}-start`}
            zone={zone}
            autoFocus
            value={start}
            onChange={setStart}
          />
        </div>
        <div className="lcr-entry-slot">
          <label htmlFor={`${id}-zone`}>Time zone it showed</label>
          <TimeZoneField
            id={`${id}-zone`}
            required={start !== ""}
            value={zone}
            onChange={setZone}
          />
        </div>
        <label>
          Length in minutes
          <input
            type="number"
            min={5}
            max={600}
            value={minutes}
            onChange={(event) => setMinutes(event.target.value)}
          />
        </label>
      </div>
      <div className="lcr-actions">
        <button type="submit" className="lcr-primary" disabled={props.busy}>
          Mark booked
        </button>
        <button type="button" className="lcr-linklike" onClick={props.onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function CouldNotBookForm(props: {
  busy: boolean;
  onSave: (reason: CouldNotBookReason) => Promise<void>;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState<CouldNotBookReason | "">("");
  return (
    <form
      className="lcr-lead-link-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (reason) void props.onSave(reason);
      }}
    >
      <fieldset className="lcr-lead-link-reasons">
        <legend className="lcr-lead-link-form-title">What stopped you?</legend>
        {COULD_NOT_BOOK_REASONS.map((value, index) => (
          <label key={value}>
            <input
              type="radio"
              name="could-not-book"
              value={value}
              checked={reason === value}
              // biome-ignore lint/a11y/noAutofocus: the form opens in place of the button that asked for it
              autoFocus={index === 0}
              onChange={() => setReason(value)}
            />
            <span>{COULD_NOT_BOOK[value].label}</span>
          </label>
        ))}
      </fieldset>
      <div className="lcr-actions">
        <button type="submit" className="lcr-primary" disabled={props.busy || reason === ""}>
          Save reason
        </button>
        <button type="button" className="lcr-linklike" onClick={props.onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** Why "Send our link" was refused, in words the rep can act on; the route names the code. */
function sendOurLinkRefusal(code: string | null): string {
  switch (code) {
    case "run_in_flight":
      return "A draft for this reply is already being prepared.";
    case "plan_changed":
    case "current_turn_event_not_reopenable":
      return "The lead wrote again, so this reply can no longer be drafted. Answer the newest message.";
    case "current_turn_sent":
      return "A reply to this message was already sent.";
    case "current_turn_answered":
      return "Someone already answered this message in Bison, so the app will not draft it.";
    case "not_lead_link":
      return "This reply is no longer waiting for a booking on their page.";
    case "send_our_link_unavailable":
      return "Drafting is not running here, so a draft cannot be asked for.";
    default:
      return `The app could not draft this reply (${code ?? "error"}).`;
  }
}

/**
 * The sheet with its data: the task read from the server, and each action posted to its route. The
 * page passes what changes the conversation (`onChange`) and where errors go.
 */
export function LeadLinkTask(props: {
  planId: string;
  planRevision: number;
  placement: "body" | "signature";
  viewerId: string | null;
  onChange: () => Promise<void>;
  onError: (message: string) => void;
  onReply: () => void;
}) {
  const planApi = usePlanApi(props.planId);
  const workspace = useWorkspace();
  const cache = useQueryClient();
  const [busy, setBusy] = useState(false);
  const key = ["lead-link", props.planId, props.planRevision];
  const view = useQuery({
    queryKey: key,
    queryFn: async () => {
      const response = await fetch(`${planApi}/lead-link`, { credentials: "same-origin" });
      if (!response.ok) throw new Error(`lead_link_${response.status}`);
      return TaskViewSchema.parse(await response.json());
    },
  });
  const send = async (
    path: string,
    body: object,
    method: "POST" | "PUT" = "POST",
  ): Promise<{ ok: boolean; error: string | null }> => {
    const response = await fetch(path, {
      method,
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (response.ok) return { ok: true, error: null };
    const failure = z
      .object({ error: z.string() })
      .safeParse(await response.json().catch(() => null));
    return { ok: false, error: failure.success ? failure.data.error : null };
  };
  const act = async (work: () => Promise<void>): Promise<void> => {
    setBusy(true);
    props.onError("");
    try {
      await work();
      await cache.invalidateQueries({ queryKey: ["lead-link", props.planId] });
    } catch (cause) {
      props.onError(cause instanceof Error ? cause.message : "Could not save that.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <LeadLinkSheet
      placement={props.placement}
      view={view.data ?? null}
      viewerId={props.viewerId}
      busy={busy}
      onOpen={() =>
        void send(`${planApi}/lead-link/open`, {}).then(() =>
          cache.invalidateQueries({ queryKey: ["lead-link", props.planId] }),
        )
      }
      onSaveHost={async (host) => {
        const campaignId = view.data?.task.campaignId;
        if (!campaignId) return false;
        let saved = false;
        await act(async () => {
          const result = await send(
            `${workspaceApi(workspace.id)}/campaigns/${encodeURIComponent(campaignId)}/call-host`,
            host,
            "PUT",
          );
          if (!result.ok)
            throw new Error(
              result.error === "sending_mailbox"
                ? "That is a sending mailbox, which nobody reads. Enter the person who takes the call."
                : "Could not save the call host. Check the name and email.",
            );
          saved = true;
        });
        return saved;
      }}
      onBooked={(booking) =>
        act(async () => {
          const result = await send(`${planApi}/lead-link/booked`, {
            commandId: crypto.randomUUID(),
            expectedRevision: props.planRevision,
            ...booking,
          });
          if (!result.ok)
            throw new Error(
              result.error === "invalid_time" || result.error === "time_needs_zone"
                ? "Enter the time with a time zone such as Europe/Warsaw, or leave the time empty."
                : result.error === "invalid_transition"
                  ? "This conversation is already marked booked, or our link was sent instead."
                  : "This conversation changed. Refresh before marking it booked.",
            );
          await props.onChange();
        })
      }
      onCouldNotBook={(reason) =>
        act(async () => {
          const result = await send(`${planApi}/lead-link/could-not-book`, { reason });
          if (!result.ok) throw new Error("Could not save that.");
        })
      }
      onSendOurLink={() =>
        void act(async () => {
          const result = await send(`${planApi}/send-our-link`, {});
          if (!result.ok) throw new Error(sendOurLinkRefusal(result.error));
          await props.onChange();
        })
      }
      onReply={props.onReply}
    />
  );
}
